import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const KINDS = [["user", "User accounts"], ["branch", "Branches"], ["product", "Products"], ["role", "Roles"]];
const total = (h) => Object.values(h).reduce((a, b) => a + b, 0);

function Row({ it, sel, onToggle, action, onAction }) {
  const used = total(it.history) > 0;
  return (
    <tr className="border-t align-top" data-testid={`cleanup-row-${it.kind}-${it.id}`}>
      <td className="py-2 pr-2 w-8">{!it.protected && <Checkbox checked={sel} onCheckedChange={(v) => onToggle(!!v)} data-testid={`cleanup-tick-${it.id}`} />}</td>
      <td className="py-2 pr-3 w-2/5"><div className="font-medium">{it.label}</div><div className="text-xs text-muted-foreground">{it.sub}{!it.is_active && " · inactive"}</div>
        {it.maybe_real && <div className="text-[11px] text-sky-700" data-testid={`cleanup-maybe-real-${it.id}`}>Looks like a real product (test kit), check before removing</div>}</td>
      <td className="py-2 pr-3 text-xs" data-testid={`cleanup-history-${it.id}`}>
        {it.protected ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-800">Protected (Super Admin / system)</span>
          : used ? <span className="text-amber-800">Has history: {Object.entries(it.history).map(([k, n]) => `${n} ${k}`).join(", ")}</span>
            : <span className="text-muted-foreground">No linked history</span>}
      </td>
      <td className="py-2 text-xs w-40">{sel && (used
        ? <span className="text-amber-800" data-testid={`cleanup-action-${it.id}`}>{it.kind === "role" ? "In use, will be skipped" : "Will be deactivated"}</span>
        : (
          <select className="rounded border bg-background px-1.5 py-1" value={action} onChange={(e) => onAction(e.target.value)} data-testid={`cleanup-action-${it.id}`}>
            <option value="delete">Delete</option>{it.kind !== "role" && <option value="deactivate">Deactivate</option>}
          </select>))}</td>
    </tr>
  );
}

// Super Admin go-live cleanup: list test-looking records, tick, then delete or deactivate.
export function TestDataCleanup({ open, onOpenChange }) {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState({});
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const load = () => { setItems(null); api.get("/admin/test-data").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(() => { if (open) { setSel({}); setConfirm(""); setResults(null); load(); } }, [open]);
  const ticked = useMemo(() => (items || []).filter((it) => sel[it.id]), [items, sel]);
  const apply = async () => {
    setBusy(true);
    try {
      const payload = ticked.map((it) => ({ kind: it.kind, id: it.id, action: total(it.history) ? "deactivate" : sel[it.id] }));
      const { data } = await api.post("/admin/test-data/apply", { items: payload, confirm });
      setResults(data.results); setSel({}); setConfirm(""); load();
      toast.success("Cleanup done, see results below");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-screen h-screen sm:rounded-none flex flex-col" data-testid="test-data-dialog">
        <DialogHeader>
          <DialogTitle>Remove test data</DialogTitle>
          <DialogDescription>Records whose name, email, code or SKU contains "test" or "example.com". Nothing is removed until you tick it and confirm. Items with history can only be deactivated.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-6 pr-1">
          {!items && <div className="text-sm text-muted-foreground">Looking for test data…</div>}
          {results && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="cleanup-results">
              <div className="font-medium mb-1">Results</div>
              <ul className="text-xs space-y-0.5">{results.map((r) => <li key={r.id}>{r.label || r.id}: {r.result}</li>)}</ul>
            </div>
          )}
          {items && KINDS.map(([k, title]) => {
            const rows = items.filter((it) => it.kind === k);
            return (
              <section key={k} data-testid={`cleanup-section-${k}`}>
                <h3 className="text-sm font-semibold mb-1">{title} <span className="text-muted-foreground font-normal">({rows.length})</span></h3>
                {rows.length === 0 ? <div className="text-xs text-muted-foreground">None found</div> : (
                  <table className="w-full text-sm table-fixed"><tbody>{rows.map((it) => (
                    <Row key={it.id} it={it} sel={!!sel[it.id]} action={sel[it.id] || "delete"}
                      onToggle={(v) => setSel((s) => { const n = { ...s }; if (v) n[it.id] = "delete"; else delete n[it.id]; return n; })}
                      onAction={(a) => setSel((s) => ({ ...s, [it.id]: a }))} />
                  ))}</tbody></table>
                )}
              </section>
            );
          })}
        </div>
        <div className="border-t pt-3 flex flex-wrap items-center gap-3">
          <span className="text-sm" data-testid="cleanup-selected-count">{ticked.length} ticked</span>
          <Input className="w-48 h-9" placeholder="Type DELETE to confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cleanup-confirm-input" />
          <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy || !ticked.length || confirm.trim() !== "DELETE"} onClick={apply} data-testid="cleanup-apply-btn">
            {busy ? "Working…" : `Apply to ${ticked.length} item(s)`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
